import React from 'react';
import { Play, Pause } from 'lucide-react';

type PlayerProps = {
  isPlaying: boolean;
  onTogglePlay: () => void;
  progressPct: number;
  currentTime: number;
  duration: number;
  title?: string;
  subtitle?: string;
  platformLinks?: Array<{ name: string; href: string }>;
};

function formatTime(seconds: number) {
  if (!isFinite(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60)
    .toString()
    .padStart(2, '0');
  return `${m}:${s}`;
}

// Brand Icons (inline SVG, currentColor-aware)
const SpotifyIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" className="block">
    <path
      fill="currentColor"
      d="M12 1.5C6.2 1.5 1.5 6.2 1.5 12S6.2 22.5 12 22.5 22.5 17.8 22.5 12 17.8 1.5 12 1.5zm5 15.3c-.2.3-.6.4-.9.2-2.4-1.5-5.4-1.9-9-1-.3.1-.7-.1-.8-.4-.1-.3.1-.7.4-.8 3.9-1 7.3-.6 10 1.1.3.2.4.6.3.9zm1.3-3.1c-.2.3-.6.4-1 .2-2.8-1.7-7.2-2.2-10.6-1.1-.3.1-.7-.1-.8-.4-.1-.3.1-.7.5-.8 3.9-1.2 8.8-.7 12 1.3.3.2.4.6.3.8zM19 10c-.2.3-.6.4-1 .2-3.3-2-9-2.2-12.2-1.1-.3.1-.7-.1-.8-.4-.1-.3.1-.7.4-.8 3.7-1.2 9.9-1 13.6 1.2.3.2.4.6.2.9z"
    />
  </svg>
);

const AppleMusicIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" className="block">
    <path
      fill="currentColor"
      d="M16.365 2.012c.123 1.28-.38 2.57-1.19 3.51-.807.94-2.112 1.66-3.372 1.56-.149-1.24.44-2.55 1.223-3.45.823-.96 2.236-1.67 3.339-1.62zM20.9 17.04c-.065 1.44.57 2.83 1.242 3.78-.906.52-1.99.99-3.156 1.01-1.384.02-1.985-.45-3.01-.45-1.025 0-1.69.44-3.035.47-1.216.03-2.14-.56-3.05-1.07-1.67-.95-3.12-2.71-3.54-4.84-.747-3.78 1.706-7.67 5.185-7.72 1.206-.02 2.248.49 3.01.49.764 0 2.077-.6 3.5-.52.596.02 2.272.24 3.35 1.8-.087.053-2.002 1.172-1.946 3.503.05 2.79 2.34 3.7 2.4 3.74z"
    />
  </svg>
);

const YouTubeMusicIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" className="block">
    <path fill="currentColor" d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zm-3 6.5l8 4.5-8 4.5v-9z" />
  </svg>
);

const DeezerIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" className="block">
    <path fill="currentColor" d="M3 15h3v3H3v-3zm4-4h3v7H7v-7zm4-2h3v9h-3V9zm4-3h3v12h-3V6zm4 1h3v11h-3V7z" />
  </svg>
);

const TidalIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" className="block">
    <path fill="currentColor" d="M6 8l2 2-2 2-2-2 2-2zm6-4l2 2-2 2-2-2 2-2zm0 8l2 2-2 2-2-2 2-2zm6-4l2 2-2 2-2-2 2-2z" />
  </svg>
);

const AmazonMusicIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" className="block">
    <path fill="currentColor" d="M4 7h3v10H4V7zm5 2h3v8H9V9zm5-3h3v11h-3V6zm5 5h3v6h-3v-6zM3 19c3.5 1.8 7.5 2.7 12 2.7 3.1 0 5.9-.4 8-1.2.3-.1.5.2.3.4-1.9 1.6-5.2 3.1-9.8 3.1-4.8 0-8.3-1.7-10.9-3.7-.3-.2-.1-.7.3-.5z" />
  </svg>
);

type PlatformDef = {
  name: string;
  href: string;
  color: string;
  Icon: React.ComponentType;
};

function buildDefaultPlatformLinks(title: string, subtitle: string): PlatformDef[] {
  const artist = subtitle?.split('•')?.[0]?.trim() || '';
  const q = encodeURIComponent([artist, title].filter(Boolean).join(' '));
  return [
    { name: 'Spotify', href: `https://open.spotify.com/search/${q}`, color: '#1DB954', Icon: SpotifyIcon },
    { name: 'Apple Music', href: `https://music.apple.com/search?term=${q}`, color: '#FC3C44', Icon: AppleMusicIcon },
    { name: 'YouTube Music', href: `https://music.youtube.com/search?q=${q}`, color: '#FF0033', Icon: YouTubeMusicIcon },
    { name: 'Deezer', href: `https://www.deezer.com/search/${q}`, color: '#FF0000', Icon: DeezerIcon },
    { name: 'Tidal', href: `https://tidal.com/search?q=${q}`, color: '#0A0A0A', Icon: TidalIcon },
    { name: 'Amazon Music', href: `https://music.amazon.com/search/${q}`, color: '#00A8E1', Icon: AmazonMusicIcon },
  ];
}

export default function Player({
  isPlaying,
  onTogglePlay,
  progressPct,
  currentTime,
  duration,
  title = 'Leren Leven',
  subtitle = 'Fresku • Album',
  platformLinks,
}: PlayerProps) {
  const defaults = buildDefaultPlatformLinks(title, subtitle);
  const platforms = defaults; // use defaults if custom not provided with icon/color

  return (
    <div className="pointer-events-none relative z-20 flex min-h-screen items-end justify-center p-4">
      <div className="pointer-events-auto w-full max-w-md rounded-xl border border-white/10 bg-black/50 backdrop-blur-md p-4 shadow-2xl">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <h1 className="truncate text-lg font-semibold text-white">{title}</h1>
            <p className="truncate text-xs text-white/70">{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={onTogglePlay}
            className="group flex h-12 w-12 items-center justify-center rounded-full bg-white text-black transition hover:scale-105 active:scale-95"
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <Pause className="h-6 w-6" />
            ) : (
              <Play className="h-6 w-6 translate-x-0.5" />
            )}
          </button>
        </div>

        <div className="mt-3">
          <div className="h-1.5 w-full rounded-full bg-white/15">
            <div
              className="h-1.5 rounded-full bg-white transition-[width]"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="mt-1.5 flex justify-between text-[10px] text-white/70">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-center gap-3 overflow-x-auto">
            {platforms.map(({ name, href, color, Icon }) => (
              <a
                key={name}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5 hover:bg-white/10 transition"
                aria-label={`Open on ${name}`}
              >
                <span className="inline-flex h-5 w-5 items-center justify-center" style={{ color }}>
                  <Icon />
                </span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

